import { cleanClientHints, parseUserAgent, type CaptureBrowser, type SessionSetup, type SessionTrail, type Ticket } from '@vd/types';
import { formatClock } from '../../lib/time';
import styles from './DevToolsPanel.module.css';

/**
 * At the top of the Session window (TOADO-911): the person's setup when they
 * reported, and what the site said of itself through `Toado.setContext()`.
 * Who reported it and their earlier reports from the visit are in the
 * window's summary line, not a card of their own (Jason, 25 Oct: a card at
 * half the window's width for one line saying "Also reported 1 other thing"
 * told a person nothing they could use). The setup is
 * the browser every capture records (TOADO-907) and, where a report brought
 * a trail, its fuller setup. Every value is the person's browser's or the
 * site's, so it is shown as text.
 */
export function SessionCards({ ticket, className }: { ticket: Ticket; className?: string }) {
  const trail = ticket.capture?.session as Partial<SessionTrail> | undefined;
  const setup = [...browserFacts(ticket.capture?.browser, trail?.setup), ...(trail?.setup ? setupFacts(trail.setup) : [])];
  const context = trail?.context && typeof trail.context === 'object' ? Object.entries(trail.context) : [];
  if (setup.length === 0 && context.length === 0) return null;

  return (
    <div className={[styles.sessionCards, className].filter(Boolean).join(' ')}>
      {setup.length > 0 && (
        <section className={styles.sessionCard} aria-label="Their setup">
          <h4>Their setup</h4>
          <dl>{setup.map(([term, value]) => <Fact key={term} term={term} value={value} />)}</dl>
        </section>
      )}
      {context.length > 0 && (
        <section className={styles.sessionCard} aria-label="From the site">
          <h4>From the site</h4>
          <dl>{context.map(([key, value]) => <Fact key={key} term={key} value={String(value)} />)}</dl>
        </section>
      )}
    </div>
  );
}

function Fact({ term, value }: { term: string; value: string }) {
  return (
    <div>
      <dt>{term}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/**
 * What every capture records of the browser, as label and value pairs: the
 * browser and system by name, and, when no trail's setup says them, the
 * window and time zone.
 */
function browserFacts(browser: CaptureBrowser | undefined, setup: SessionSetup | undefined): Array<[string, string]> {
  if (!browser) return [];
  const hints = cleanClientHints(browser);
  const parsed = parseUserAgent(browser.userAgent, hints);
  const name = (part: { name: string; version?: string } | undefined) => (part ? [part.name, part.version].filter(Boolean).join(' ') : '');
  const facts: Array<[string, string]> = [];
  if (name(parsed.browser)) facts.push(['Browser', name(parsed.browser)]);
  if (name(parsed.os)) facts.push(['System', [name(parsed.os), hints.architecture].filter(Boolean).join(', ')]);
  if (hints.model) facts.push(['Model', hints.model]);
  if (!setup && browser.viewport) {
    const ratio = browser.devicePixelRatio && browser.devicePixelRatio !== 1 ? ` at ${browser.devicePixelRatio}×` : '';
    facts.push(['Window', `${browser.viewport.width}×${browser.viewport.height}${ratio}`]);
  } else if (browser.devicePixelRatio && browser.devicePixelRatio !== 1) {
    facts.push(['Pixel ratio', `${browser.devicePixelRatio}×`]);
  }
  const looks = [
    browser.colorScheme === 'dark' ? 'dark mode' : browser.colorScheme === 'light' ? 'light mode' : '',
    browser.reducedMotion ? 'reduced motion' : '',
  ].filter(Boolean);
  if (looks.length > 0) facts.push(['Appearance', looks.join(', ')]);
  if (!setup?.timezone && browser.timezone) facts.push(['Time zone', browser.timezone]);
  return facts;
}

/** The setup as label and value pairs, leaving out what the browser did not say. */
function setupFacts(setup: SessionSetup): Array<[string, string]> {
  const facts: Array<[string, string]> = [];
  const size = (box: { width: number; height: number } | undefined) =>
    box && Number.isFinite(box.width) && Number.isFinite(box.height) ? `${Math.round(box.width)}×${Math.round(box.height)}` : '';
  facts.push(['Device', `${setup.deviceType}${setup.touch ? ', touch' : ''}`]);
  const screen = size(setup.screen);
  const window = size(setup.window);
  if (screen || window) facts.push(['Screen and window', [screen && `screen ${screen}`, window && `window ${window}`].filter(Boolean).join(', ')]);
  if (Array.isArray(setup.languages) && setup.languages.length > 0) facts.push(['Languages', setup.languages.join(', ')]);
  const local = localTime(setup.localTime);
  if (local) facts.push(['Their time', `${local}${setup.timezone ? ` (${setup.timezone})` : ''}`]);
  const connection = setup.connection;
  if (connection) {
    const parts = [connection.type, connection.effectiveType, typeof connection.downlinkMbps === 'number' ? `${connection.downlinkMbps} Mb/s` : '', connection.saveData ? 'data saver on' : '']
      .filter((part): part is string => typeof part === 'string' && part.length > 0);
    if (parts.length > 0) facts.push(['Connection', parts.join(', ')]);
  }
  const hardware = [typeof setup.deviceMemory === 'number' ? `${setup.deviceMemory} GB memory` : '', typeof setup.hardwareConcurrency === 'number' ? `${setup.hardwareConcurrency} processors` : ''].filter(Boolean);
  if (hardware.length > 0) facts.push(['Hardware', hardware.join(', ')]);
  const display = [setup.forcedColors ? 'forced colors' : '', setup.moreContrast ? 'more contrast' : ''].filter(Boolean);
  if (display.length > 0) facts.push(['Display', display.join(', ')]);
  facts.push(['Cookies and storage', `${setup.cookies ? 'cookies on' : 'cookies off'}, ${setup.storage ? 'storage available' : 'no storage'}`]);
  return facts;
}

/**
 * Their wall clock when they reported, as they saw it: the ISO time they sent
 * carries their offset, so it is read in that offset, not ours.
 */
function localTime(value: unknown): string {
  if (typeof value !== 'string') return '';
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return '';
  const hour = Number(match[2]);
  const at = Date.parse(value);
  const clock = `${((hour + 11) % 12) + 1}:${match[3]} ${hour < 12 ? 'am' : 'pm'}`;
  return Number.isFinite(at) ? `${clock} their time, ${formatClock(at, { ms: false })} yours` : `${clock} their time`;
}
